import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { computeShiftLengthHours, ensureTimeHasSeconds } from 'src/app/_helpers/time-date-convertor';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';
import { ShiftType } from 'src/app/enums/shiftTypeValue.enum';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { Shift } from 'src/app/models/shift.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ShiftService } from 'src/app/services/shift.service';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { DateFormField, HierarchicalSelectionFormField, NumberFormField, SingleSelectionFormField, TextAreaFormField, TextFormField, TimeFormField } from 'src/app/shared/form/formField.model';
import { ILookup } from 'src/app/shared/form/lookup.model';
import nameOf from 'src/app/shared/form/nameOf.helper';

@Component({
  standalone: false,
    selector: 'app-shift-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class ShiftDialogComponent extends FormComponent<Shift> implements OnInit {
    organizationLevels: OrganizationLevel[];
    shiftTypes: ILookup[] = Object.keys(ShiftType)
        .filter(key => !isNaN(Number(key)))
        .map(key => ({
            id: Number(key),
            name: ShiftType[Number(key)].replace('Shift_', '')
        }));
    
    private shiftLengthField: NumberFormField;

    constructor(
        dialogRef: MatDialogRef<ShiftDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        shiftService: ShiftService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector
    ) {
        super(dialogRef, shiftService, data, 'Shift', injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        const orgPayload = JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<OrganizationLevel>(x => x.orgLevelType),
                Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`
            })
        });
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(orgPayload)
        }).subscribe(result => {
            this.entity = result.entity;
            // Normalize time values from stored format (HH:MM:SS) to displayable format (HH:MM)
            if (this.entity?.startTime) {
                this.entity.startTime = this.dateFormatService.parseUserTimeToHM(this.entity.startTime) ?? this.entity.startTime;
            }
            if (this.entity?.endTime) {
                this.entity.endTime = this.dateFormatService.parseUserTimeToHM(this.entity.endTime) ?? this.entity.endTime;
            }
            if (this.entity?.effectiveStartDate) {
                this.entity.effectiveStartDate = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.effectiveStartDate);
            }
            if (this.entity?.effectiveEndDate) {
                this.entity.effectiveEndDate = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.effectiveEndDate);
            }
            this.organizationLevels = result.organizationLevels;
            this.initFields();
            this.isFormLoading = false;
        },
            () => {
                this.toastService.displayErrorMessage('Error loading shift data', true);
                this.isFormLoading = false;
            }
        );
    }

    getNewObject(): Shift {
        return new Shift();
    }

    validate(): boolean {
        return true;
    }

    onBeforeSave(model: Shift): void {
        // Ensure time values are saved in HH:MM:SS format
        if (model.startTime) {
            model.startTime = ensureTimeHasSeconds(model.startTime);
        }
        if (model.endTime) {
            model.endTime = ensureTimeHasSeconds(model.endTime);
        }
        if (model.effectiveStartDate) {
            model.effectiveStartDate = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.effectiveStartDate);
        }
        if (model.effectiveEndDate) {
            model.effectiveEndDate = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.effectiveEndDate);
        }
    }

    private isShiftLengthFieldReadonly(): boolean {
        return !!(this.entity?.startTime && this.entity?.endTime);
    }

    private updateShiftLengthFieldState(): void {
        if (!this.shiftLengthField) {
            return;
        }

        const isReadonly = this.isShiftLengthFieldReadonly();
        this.shiftLengthField.readonly = isReadonly;

        // If both times are set, compute and update the shift length
        if (isReadonly) {
            const computedLength = computeShiftLengthHours(this.entity?.startTime, this.entity?.endTime);
            if (computedLength !== null) {
                this.entity.shiftLengthHours = computedLength;
            }
        }
    }

    private validateShiftLength(value: unknown): string | null {
        if (value === undefined || value === null) {
            return null;
        }

        const numValue = Number(value);
        
        if (numValue < 0) {
            return 'Shift length cannot be negative';
        }

        return null;
    }

    initFields(): void {
        this.fields = [
            new TextFormField({
                property: nameOf<Shift>(x => x.name),
                name: 'Name'
            }),
            new TextFormField({
                property: nameOf<Shift>(x => x.code),
                name: 'Code'
            }),
            new TextAreaFormField({
                property: nameOf<Shift>(x => x.description),
                name: 'Description',
                required: false
            }),
            new SingleSelectionFormField({
                property: nameOf<Shift>(x => x.shiftType),
                options: this.shiftTypes,
                name: 'Shift Type',
                required: false
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<Shift>(x => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: 'Organization Level',
                required: true
            }),
            new TimeFormField({
                property: nameOf<Shift>(x => x.startTime),
                name: 'Start Time',
                required: false,
                onChange: () => this.updateShiftLengthFieldState()
            }),
            new TimeFormField({
                property: nameOf<Shift>(x => x.endTime),
                name: 'End Time',
                required: false,
                onChange: () => this.updateShiftLengthFieldState()
            }),
            new NumberFormField({
                property: nameOf<Shift>(x => x.shiftLengthHours),
                name: 'Shift Length (hrs)',
                required: false,
                readonly: this.isShiftLengthFieldReadonly(),
                min: 0,
                max: 99.99,
                validator: (value: unknown) => this.validateShiftLength(value)
            }),
            new NumberFormField({
                property: nameOf<Shift>(x => x.maxWeeklyHours),
                name: 'Max Weekly Hours',
                required: false
            }),
            new NumberFormField({
                property: nameOf<Shift>(x => x.averageWeeklyHours),
                name: 'Average Weekly Hours',
                required: false
            }),
            new DateFormField({
                property: nameOf<Shift>(x => x.effectiveStartDate),
                name: 'Effective Start Date',
                required: false
            }),
            new DateFormField({
                property: nameOf<Shift>(x => x.effectiveEndDate),
                name: 'Effective End Date',
                required: false
            }),
        ];

        // Store reference to shiftLengthField for later updates
        this.shiftLengthField = this.fields.find(f => f.property === nameOf<Shift>(x => x.shiftLengthHours)) as NumberFormField;
        this.updateShiftLengthFieldState();
    }
}
